import React, { useCallback, useEffect, useState } from 'react';
import { Plus, BellRing, ChevronDown, Check, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { oapi } from '@/lib/officialsApi';
import { NewRequestDialog } from './NewRequestDialog';

const fmtRange = (r) => [r.start_date, r.end_date].filter(Boolean).map(d => new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })).join(' – ');

const RequestRow = ({ r, onFollowUp }) => {
  const [open, setOpen] = useState(false);
  const pct = r.total ? Math.round((r.responded_count / r.total) * 100) : 0;
  const pending = r.total - r.responded_count;
  return (
    <div className="py-3" data-testid={`avail-request-${r.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]">
          <div className="font-semibold text-slate-900">{r.title}</div>
          <div className="text-xs text-slate-500">{fmtRange(r) || 'Any dates'} · sent {new Date(r.created_at).toLocaleDateString()}{r.follow_ups ? ` · ${r.follow_ups} follow-up${r.follow_ups === 1 ? '' : 's'}` : ''}</div>
        </div>
        <div className="w-40">
          <div className="flex justify-between text-[11px] font-semibold text-slate-600"><span data-testid={`avail-request-count-${r.id}`}>{r.responded_count}/{r.total} replied</span><span>{pct}%</span></div>
          <div className="h-1.5 rounded-full bg-slate-100 mt-1 overflow-hidden"><div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} /></div>
        </div>
        <Button variant="outline" size="sm" disabled={!pending} onClick={() => onFollowUp(r)} data-testid={`avail-request-followup-${r.id}`}>
          <BellRing className="w-4 h-4 mr-1.5" />Remind {pending || ''}
        </Button>
        <button type="button" onClick={() => setOpen(o => !o)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid={`avail-request-toggle-${r.id}`}>
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {open && (
        <ul className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1">
          {r.people.map(p => (
            <li key={p.id} className="flex items-center gap-2 text-sm">
              {p.responded_at ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Clock className="w-3.5 h-3.5 text-amber-500" />}
              <span className="truncate">{p.name}</span>
              {!p.has_account && <span className="text-[10px] text-slate-400">no account yet</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const AvailabilityRequests = ({ orgId, officials }) => {
  const [reqs, setReqs] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => {
    oapi('/officials/availability-requests', { orgId }).then(d => setReqs(d.requests)).catch(e => toast.error(e.message));
  }, [orgId]);
  useEffect(() => { load(); }, [load]);
  const followUp = async (r) => {
    try {
      const x = await oapi(`/officials/availability-requests/${r.id}/follow-up`, { method: 'POST', orgId, body: {} });
      toast.success(`Reminder sent to ${x.emails_sent} of ${x.pending} who haven't replied`);
      load();
    } catch (e) { toast.error(e.message); }
  };
  return (
    <section className="bg-white border rounded-2xl px-4 py-3" data-testid="availability-requests">
      <div className="flex flex-wrap items-center gap-2">
        <div className="mr-auto">
          <h3 className="text-lg font-black uppercase text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>Availability requests</h3>
          <p className="text-xs text-slate-500">Email your officials asking them to fill in their availability for a date range.</p>
        </div>
        <Button size="sm" className="bg-[#00247D] hover:bg-[#001A5C]" onClick={() => setOpen(true)} data-testid="new-avail-request-btn"><Plus className="w-4 h-4 mr-1.5" />New request</Button>
      </div>
      <div className="divide-y mt-1">
        {(reqs || []).map(r => <RequestRow key={r.id} r={r} onFollowUp={followUp} />)}
        {reqs && reqs.length === 0 && <p className="py-4 text-sm text-slate-500">No requests sent yet.</p>}
      </div>
      <NewRequestDialog open={open} orgId={orgId} officials={officials} onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); }} />
    </section>
  );
};
